'use client';

import React, { useState } from 'react';
import { X, ListPlus, MessageSquare, CheckCircle, Mic, Trash2, Send, Clock, ChevronUp, Tag } from 'lucide-react';

export type GossipStatus = 'pendiente' | 'en-cola' | 'contando' | 'contado';

export interface GossipComment {
  id: string;
  text: string;
  timestamp: string;
}

export interface GossipRow {
  id: string;
  text: string;
  category: string;
  votes: number;
  status: GossipStatus;
  nickname: string;
  time: string;
  comments?: GossipComment[];
}

interface GossipDetailDrawerProps {
  gossip: GossipRow | null;
  onClose: () => void;
  onStatusChange: (id: string, status: GossipStatus) => void;
  onAddComment: (id: string, comment: string) => void;
  onDeleteComment: (gossipId: string, commentId: string) => void;
}

const categoryColors: Record<string, string> = {
  Amor: 'bg-pink-100 text-pink-700 border-pink-200',
  Trabajo: 'bg-blue-100 text-blue-700 border-blue-200',
  Familia: 'bg-orange-100 text-orange-700 border-orange-200',
  Vecinos: 'bg-green-100 text-green-700 border-green-200',
  Famosos: 'bg-purple-100 text-purple-700 border-purple-200',
  Otro: 'bg-gray-100 text-gray-700 border-gray-200',
};

const STATUS_OPTIONS: { value: GossipStatus; label: string; color: string }[] = [
  { value: 'pendiente', label: '📬 Pendiente', color: 'status-pendiente' },
  { value: 'en-cola', label: '⏳ En Cola', color: 'status-en-cola' },
  { value: 'contando', label: '🎙️ Contando', color: 'status-contando' },
  { value: 'contado', label: '✅ Contado', color: 'status-contado' },
];

export default function GossipDetailDrawer({
  gossip,
  onClose,
  onStatusChange,
  onAddComment,
  onDeleteComment,
}: GossipDetailDrawerProps) {
  const [commentText, setCommentText] = useState('');
  const [deleteCommentConfirm, setDeleteCommentConfirm] = useState<string | null>(null);

  if (!gossip) return null;

  const handleAddComment = () => {
    const trimmed = commentText.trim();
    if (!trimmed) return;
    onAddComment(gossip.id, trimmed);
    setCommentText('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleAddComment();
    }
  };

  const currentStatus = STATUS_OPTIONS.find((s) => s.value === gossip.status);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-lg bg-card border-l border-border shadow-2xl z-50 flex flex-col slide-up overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-muted/30 shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-lg">🧶</span>
            <div>
              <h2 className="font-bold text-foreground text-base leading-tight">Detalle del Chisme</h2>
              <p className="text-xs text-muted-foreground">ID: {gossip.id}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto scrollbar-thin px-6 py-5 flex flex-col gap-6">

          {/* Meta row */}
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${categoryColors[gossip.category] || categoryColors['Otro']}`}>
              <Tag size={10} className="inline mr-1" />
              {gossip.category}
            </span>
            <span className="flex items-center gap-1 text-xs font-bold text-foreground bg-muted px-2.5 py-1 rounded-full">
              <ChevronUp size={12} className="text-primary" />
              {gossip.votes} votos
            </span>
            <span className="flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2.5 py-1 rounded-full">
              <Clock size={10} />
              {gossip.time}
            </span>
            <span className="text-xs text-muted-foreground bg-muted px-2.5 py-1 rounded-full">
              👤 {gossip.nickname}
            </span>
          </div>

          {/* Full gossip text */}
          <div className="bg-muted/50 border border-border rounded-2xl p-5">
            <p className="text-sm font-semibold text-muted-foreground mb-2 uppercase tracking-wide text-xs">El Chisme Completo</p>
            <p className="text-foreground text-sm leading-relaxed">{gossip.text}</p>
          </div>

          {/* Status & Queue Actions */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Estado & Cola de Transmisión</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => onStatusChange(gossip.id, opt.value)}
                  className={`text-xs font-semibold px-3 py-2 rounded-xl border transition-all ${
                    gossip.status === opt.value
                      ? `${opt.color} ring-2 ring-primary/30 scale-105`
                      : 'bg-muted text-muted-foreground border-border hover:border-primary/30 hover:text-primary'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Quick action buttons */}
            <div className="flex gap-2">
              <button
                onClick={() => onStatusChange(gossip.id, 'en-cola')}
                disabled={gossip.status === 'en-cola' || gossip.status === 'contando' || gossip.status === 'contado'}
                className="flex-1 flex items-center justify-center gap-2 bg-secondary/10 text-secondary border border-secondary/20 hover:bg-secondary/20 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl px-3 py-2.5 text-xs font-semibold transition-all"
              >
                <ListPlus size={14} />
                Agregar a Cola
              </button>
              <button
                onClick={() => onStatusChange(gossip.id, 'contando')}
                disabled={gossip.status === 'contando' || gossip.status === 'contado'}
                className="flex-1 flex items-center justify-center gap-2 bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl px-3 py-2.5 text-xs font-semibold transition-all"
              >
                <Mic size={14} />
                Contar Ahora
              </button>
              <button
                onClick={() => onStatusChange(gossip.id, 'contado')}
                disabled={gossip.status === 'contado'}
                className="flex items-center justify-center gap-2 bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl px-3 py-2.5 text-xs font-semibold transition-all"
              >
                <CheckCircle size={14} />
                Contado
              </button>
            </div>
          </div>

          {/* Comments section */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare size={14} className="text-primary" />
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Comentarios de la Anfitriona
              </p>
              {(gossip.comments?.length ?? 0) > 0 && (
                <span className="bg-primary/10 text-primary text-xs font-bold px-2 py-0.5 rounded-full">
                  {gossip.comments?.length}
                </span>
              )}
            </div>

            {/* Existing comments */}
            {(gossip.comments?.length ?? 0) > 0 ? (
              <div className="flex flex-col gap-2 mb-4">
                {gossip.comments?.map((comment) => (
                  <div
                    key={comment.id}
                    className="bg-primary/5 border border-primary/15 rounded-xl px-4 py-3 group relative"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-bold text-primary">🧶 Anfitriona</span>
                          <span className="text-xs text-muted-foreground">{comment.timestamp}</span>
                        </div>
                        <p className="text-sm text-foreground leading-relaxed">{comment.text}</p>
                      </div>
                      {deleteCommentConfirm === comment.id ? (
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={() => { onDeleteComment(gossip.id, comment.id); setDeleteCommentConfirm(null); }}
                            className="text-xs bg-red-500 text-white px-2 py-1 rounded-lg font-semibold hover:bg-red-600 transition-colors"
                          >
                            Sí
                          </button>
                          <button
                            onClick={() => setDeleteCommentConfirm(null)}
                            className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-lg font-semibold hover:bg-border transition-colors"
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteCommentConfirm(comment.id)}
                          className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-50 transition-all shrink-0"
                          aria-label="Eliminar comentario"
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 bg-muted/30 rounded-xl border border-dashed border-border mb-4">
                <p className="text-2xl mb-1">💬</p>
                <p className="text-xs text-muted-foreground">Sin comentarios aún</p>
              </div>
            )}

            {/* Add comment */}
            <div className="bg-muted/40 border border-border rounded-xl p-3">
              <p className="text-xs text-muted-foreground mb-2 font-medium">Dejar nota o comentario al chisme:</p>
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={3}
                placeholder="Ej: ¡Este es perfecto para el live de hoy! 🔥 o Necesito más detalles..."
                className="w-full bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none transition"
              />
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-muted-foreground">Ctrl+Enter para enviar</p>
                <button
                  onClick={handleAddComment}
                  disabled={!commentText.trim()}
                  className="flex items-center gap-1.5 bg-primary text-white text-xs font-semibold px-3 py-2 rounded-xl hover:bg-[#c2185b] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  <Send size={12} />
                  Comentar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
